import React, { useState, useEffect } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SparkleIcon, XIcon, CheckCircleIcon, StorefrontIcon, PlusIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import { sortByUnit } from "@/lib/unitSort";

/**
 * Review dialog for AI-parsed parts-supplier invoices.
 * Confirms vendor + parts list, then hits /parts/save-invoice to
 * upsert vendor as repair_shop and add/increment parts in inventory.
 */
export default function AiPartsReview({ open, onOpenChange, parsed, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [trucks, setTrucks] = useState([]);
  const [trailers, setTrailers] = useState([]);
  const [selected, setSelected] = useState(new Set());  // indices of parts checked for bulk assign
  const [bulkUnit, setBulkUnit] = useState("stock");    // "stock" | "truck:<id>" | "trailer:<id>"
  const [vendor, setVendor] = useState({
    vendor_name: parsed?.extracted?.vendor_name || "",
    vendor_address: parsed?.extracted?.vendor_address || "",
    vendor_phone: parsed?.extracted?.vendor_phone || "",
    vendor_email: parsed?.extracted?.vendor_email || "",
    invoice_number: parsed?.extracted?.invoice_number || "",
    invoice_date: parsed?.extracted?.invoice_date || new Date().toISOString().slice(0, 10),
  });
  const [parts, setParts] = useState(parsed?.extracted?.parts || []);

  // Load trucks + trailers once for the Unit dropdown
  useEffect(() => {
    if (!open) return;
    Promise.all([api.get("/trucks"), api.get("/trailers")])
      .then(([t, tr]) => { setTrucks(t.data || []); setTrailers(tr.data || []); })
      .catch(() => {});
  }, [open]);

  React.useEffect(() => {
    if (!parsed) return;
    setVendor({
      vendor_name: parsed.extracted?.vendor_name || "",
      vendor_address: parsed.extracted?.vendor_address || "",
      vendor_phone: parsed.extracted?.vendor_phone || "",
      vendor_email: parsed.extracted?.vendor_email || "",
      invoice_number: parsed.extracted?.invoice_number || "",
      invoice_date: parsed.extracted?.invoice_date || new Date().toISOString().slice(0, 10),
    });
    setParts(parsed.extracted?.parts || []);
    setSelected(new Set());
    setBulkUnit("stock");
  }, [parsed]);

  const toggleSelect = (i) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  };
  const toggleSelectAll = () => {
    setSelected((prev) => (prev.size === parts.length ? new Set() : new Set(parts.map((_, i) => i))));
  };
  const applyBulkUnit = () => {
    if (selected.size === 0) { toast.error("No rows selected"); return; }
    let uid = "", utype = "", unum = "";
    if (bulkUnit !== "stock") {
      const [ut, id] = bulkUnit.split(":");
      const src = ut === "truck" ? trucks : trailers;
      const found = src.find((x) => x.id === id);
      uid = id; utype = ut; unum = found?.unit_number || "";
    }
    setParts((prev) => prev.map((row, idx) => selected.has(idx) ? { ...row, assigned_unit_id: uid, assigned_unit_type: utype, assigned_unit_number: unum } : row));
    toast.success(`Assigned ${selected.size} row${selected.size > 1 ? "s" : ""} to ${bulkUnit === "stock" ? "Stock" : (unum ? `Unit ${unum}` : "unit")}`);
    setSelected(new Set());
  };

  const updatePart = (i, field, value) => {
    setParts(parts.map((p, idx) => {
      if (idx !== i) return p;
      const next = { ...p, [field]: value };
      // Auto-recalculate line total when qty or unit_cost changes so an admin
      // never has to update three fields manually. If they explicitly edit
      // total we honor that (no auto-recalc).
      if (field === "quantity" || field === "unit_cost") {
        const q = Number(field === "quantity" ? value : next.quantity) || 0;
        const uc = Number(field === "unit_cost" ? value : next.unit_cost) || 0;
        next.total = Math.round(q * uc * 100) / 100;
      }
      return next;
    }));
  };

  // Split one line into two — keeps unit_cost, splits qty (default: half & half)
  // Great for "bought 4 tires: 2 for a truck, 2 for stock" → click Split.
  const splitLine = (i) => {
    setParts((prev) => {
      const src = prev[i];
      const q = Number(src.quantity) || 0;
      if (q < 2) { toast.error("Line must have qty >= 2 to split"); return prev; }
      const uc = Number(src.unit_cost) || 0;
      const half1 = Math.floor(q / 2);
      const half2 = q - half1;
      const line1 = { ...src, quantity: half1, total: Math.round(half1 * uc * 100) / 100 };
      const line2 = { ...src, quantity: half2, total: Math.round(half2 * uc * 100) / 100, assigned_unit_id: "", assigned_unit_type: "", assigned_unit_number: "" };
      const next = [...prev];
      next.splice(i, 1, line1, line2);
      return next;
    });
    toast.success("Row split — set the second row's unit if needed");
  };

  const save = async () => {
    if (!vendor.vendor_name) {
      toast.error("Vendor name is required");
      return;
    }
    if (parts.length === 0) {
      toast.error("Add at least one part");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...vendor,
        invoice_file_id: parsed?.invoice_file_id || null,
        invoice_file_name: parsed?.invoice_file_name || null,
        invoice_file_type: parsed?.invoice_file_type || null,
        parts: parts.map((p) => ({
          name: p.name || "",
          part_number: p.part_number || "",
          quantity: Number(p.quantity) || 0,
          unit_cost: Number(p.unit_cost) || 0,
          total: Number(p.total) || 0,
          category: p.category || "other",
          assigned_unit_id: p.assigned_unit_id || "",
          assigned_unit_type: p.assigned_unit_type || "",
          assigned_unit_number: p.assigned_unit_number || "",
        })),
      };
      const { data } = await api.post("/parts/save-invoice", payload);
      const allocMsg = data.allocations_created ? ` · ${data.allocations_created} reserved for units` : "";
      toast.success(`Saved — ${data.added.length} added, ${data.updated.length} updated${allocMsg}`);
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!parsed) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl rounded-sm max-h-[90vh] overflow-y-auto" data-testid="ai-parts-review">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight flex items-center gap-2">
            <SparkleIcon size={20} weight="fill" className="text-blue-600" />
            Review parts invoice
            <span className="text-xs font-mono text-slate-500 ml-2">{parsed.filename}</span>
          </DialogTitle>
        </DialogHeader>

        {/* Vendor block */}
        <div className="p-4 bg-slate-50 border border-slate-200 rounded-sm mb-4">
          <div className="text-[10px] uppercase font-mono text-slate-500 font-bold mb-2 flex items-center gap-1">
            <StorefrontIcon size={12} weight="bold" /> Vendor / Shop (will be added to Repair Shops if new)
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Name *</Label>
              <Input value={vendor.vendor_name} onChange={(e) => setVendor({ ...vendor, vendor_name: e.target.value })} className="mt-1 rounded-sm" data-testid="apr-vendor-name" />
            </div>
            <div>
              <Label className="text-xs">Phone</Label>
              <Input value={vendor.vendor_phone} onChange={(e) => setVendor({ ...vendor, vendor_phone: e.target.value })} className="mt-1 rounded-sm font-mono" data-testid="apr-vendor-phone" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Address</Label>
              <Input value={vendor.vendor_address} onChange={(e) => setVendor({ ...vendor, vendor_address: e.target.value })} className="mt-1 rounded-sm" data-testid="apr-vendor-addr" />
            </div>
            <div>
              <Label className="text-xs">Invoice #</Label>
              <Input value={vendor.invoice_number} onChange={(e) => setVendor({ ...vendor, invoice_number: e.target.value })} className="mt-1 rounded-sm font-mono" data-testid="apr-invoice" />
            </div>
            <div>
              <Label className="text-xs">Date of purchase</Label>
              <Input type="date" value={vendor.invoice_date} onChange={(e) => setVendor({ ...vendor, invoice_date: e.target.value })} className="mt-1 rounded-sm" data-testid="apr-date" />
            </div>
          </div>
        </div>

        {/* Parts table */}
        <div className="text-[10px] uppercase font-mono text-slate-500 font-bold mb-2">Parts on invoice ({parts.length}) — will update inventory stock & cost</div>
        <div className="text-[11px] text-slate-600 mb-2 bg-blue-50 border border-blue-200 rounded-sm px-2 py-1.5">
          💡 Assign each part to a specific <b>Unit</b> if it was bought for that truck/trailer — we&apos;ll suggest it when you log an in-house repair for that unit. Leave as <b>Stock</b> for general shelf inventory.
        </div>

        {/* Bulk assign bar */}
        {parts.length > 1 && (
          <div className="flex items-center gap-2 mb-2 bg-slate-50 border border-slate-300 rounded-sm px-3 py-2" data-testid="apr-bulk-bar">
            <span className="font-mono text-[10px] uppercase text-slate-600 font-bold">
              {selected.size === 0 ? "Bulk assign:" : `${selected.size} selected`}
            </span>
            <select
              value={bulkUnit}
              onChange={(e) => setBulkUnit(e.target.value)}
              className="h-8 border border-slate-300 rounded-sm text-xs px-1 bg-white flex-1 max-w-xs"
              data-testid="apr-bulk-unit"
            >
              <option value="stock">📦 Stock (shelf)</option>
              {trucks.length > 0 && <optgroup label="Trucks">
                {sortByUnit(trucks).map((t) => <option key={`bt-${t.id}`} value={`truck:${t.id}`}>🚚 Unit {t.unit_number} · {t.make} {t.model}</option>)}
              </optgroup>}
              {trailers.length > 0 && <optgroup label="Trailers">
                {sortByUnit(trailers).map((t) => <option key={`btr-${t.id}`} value={`trailer:${t.id}`}>🚛 Trailer {t.unit_number}</option>)}
              </optgroup>}
            </select>
            <Button
              type="button"
              onClick={applyBulkUnit}
              disabled={selected.size === 0}
              className="h-8 rounded-sm bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold px-3 disabled:opacity-40"
              data-testid="apr-bulk-apply"
            >
              Apply to {selected.size || "…"}
            </Button>
          </div>
        )}

        <div className="border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-100">
              <tr className="text-left">
                <th className="px-2 py-2 w-8">
                  <input
                    type="checkbox"
                    checked={parts.length > 0 && selected.size === parts.length}
                    onChange={toggleSelectAll}
                    aria-label="Select all"
                    data-testid="apr-select-all"
                  />
                </th>
                <th className="px-2 py-2 font-mono text-[10px] uppercase text-slate-500">Part #</th>
                <th className="px-2 py-2 font-mono text-[10px] uppercase text-slate-500">Name</th>
                <th className="px-2 py-2 font-mono text-[10px] uppercase text-slate-500">Assigned Unit</th>
                <th className="px-2 py-2 font-mono text-[10px] uppercase text-slate-500">Category</th>
                <th className="px-2 py-2 font-mono text-[10px] uppercase text-slate-500 text-right">Qty</th>
                <th className="px-2 py-2 font-mono text-[10px] uppercase text-slate-500 text-right">Unit $</th>
                <th className="px-2 py-2 font-mono text-[10px] uppercase text-slate-500 text-right">Total</th>
                <th className="px-2 py-2 font-mono text-[10px] uppercase text-slate-500">Match</th>
                <th className="px-2 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {parts.map((p, i) => {
                const unitValue = p.assigned_unit_id ? `${p.assigned_unit_type}:${p.assigned_unit_id}` : "stock";
                const onUnitChange = (e) => {
                  const v = e.target.value;
                  if (v === "stock") {
                    updatePart(i, "assigned_unit_id", "");
                    setParts((prev) => prev.map((row, idx) => idx === i ? { ...row, assigned_unit_id: "", assigned_unit_type: "", assigned_unit_number: "" } : row));
                    return;
                  }
                  const [ut, uid] = v.split(":");
                  const source = ut === "truck" ? trucks : trailers;
                  const found = source.find((x) => x.id === uid);
                  setParts((prev) => prev.map((row, idx) => idx === i ? { ...row, assigned_unit_id: uid, assigned_unit_type: ut, assigned_unit_number: found?.unit_number || "" } : row));
                };
                return (
                <tr key={i} className={`border-t border-slate-200 ${selected.has(i) ? "bg-blue-50/50" : ""}`} data-testid={`apr-part-${i}`}>
                  <td className="p-1 text-center">
                    <input
                      type="checkbox"
                      checked={selected.has(i)}
                      onChange={() => toggleSelect(i)}
                      aria-label={`Select row ${i + 1}`}
                      data-testid={`apr-select-${i}`}
                    />
                  </td>
                  <td className="p-1 w-32"><Input value={p.part_number || ""} onChange={(e) => updatePart(i, "part_number", e.target.value)} className="h-8 rounded-sm font-mono text-xs" /></td>
                  <td className="p-1"><Input value={p.name || ""} onChange={(e) => updatePart(i, "name", e.target.value)} className="h-8 rounded-sm text-xs" /></td>
                  <td className="p-1 w-40">
                    <select
                      value={unitValue}
                      onChange={onUnitChange}
                      className="h-8 w-full border border-slate-300 rounded-sm text-xs px-1 bg-white"
                      data-testid={`apr-unit-${i}`}
                    >
                      <option value="stock">📦 Stock (shelf)</option>
                      {trucks.length > 0 && <optgroup label="Trucks">
                        {sortByUnit(trucks).map((t) => <option key={`t-${t.id}`} value={`truck:${t.id}`}>🚚 Unit {t.unit_number} · {t.make} {t.model}</option>)}
                      </optgroup>}
                      {trailers.length > 0 && <optgroup label="Trailers">
                        {sortByUnit(trailers).map((t) => <option key={`tr-${t.id}`} value={`trailer:${t.id}`}>🚛 Trailer {t.unit_number} · {(t.trailer_type||"").replace(/_/g," ")}</option>)}
                      </optgroup>}
                    </select>
                  </td>
                  <td className="p-1 w-28"><Input value={p.category || ""} onChange={(e) => updatePart(i, "category", e.target.value)} className="h-8 rounded-sm text-xs" /></td>
                  <td className="p-1 w-16"><Input value={p.quantity ?? ""} onChange={(e) => updatePart(i, "quantity", e.target.value)} className="h-8 rounded-sm font-mono text-xs text-right" /></td>
                  <td className="p-1 w-20"><Input value={p.unit_cost ?? ""} onChange={(e) => updatePart(i, "unit_cost", e.target.value)} className="h-8 rounded-sm font-mono text-xs text-right" /></td>
                  <td className="p-1 w-20"><Input value={p.total ?? ""} onChange={(e) => updatePart(i, "total", e.target.value)} className="h-8 rounded-sm font-mono text-xs text-right" /></td>
                  <td className="p-1 text-[10px] font-mono">
                    {p.existing_part_id ? (
                      <span className="text-emerald-700">EXISTING · stock {p.existing_stock ?? 0} → +{p.quantity ?? 0}</span>
                    ) : (
                      <span className="text-blue-700">NEW</span>
                    )}
                  </td>
                  <td className="p-1">
                    <button type="button" onClick={() => setParts(parts.filter((_, idx) => idx !== i))} className="text-red-600" data-testid={`apr-remove-${i}`}>
                      <XIcon size={12} weight="bold" />
                    </button>
                  </td>
                </tr>
              );})}
              {parts.length === 0 && (
                <tr><td colSpan={10} className="text-center py-4 text-slate-500 text-xs">No parts extracted. Click &quot;Add row&quot; to enter manually.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <button
          type="button"
          onClick={() => setParts([...parts, { part_number: "", name: "", category: "other", quantity: 1, unit_cost: 0, total: 0, assigned_unit_id: "", assigned_unit_type: "", assigned_unit_number: "" }])}
          className="mt-2 text-xs text-blue-700 hover:text-blue-900 font-semibold inline-flex items-center gap-1"
          data-testid="apr-add-row"
        >
          <PlusIcon size={12} weight="bold" /> Add row manually
        </button>

        <DialogFooter className="pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm font-bold"
            data-testid="apr-save"
          >
            <CheckCircleIcon size={16} weight="bold" className="mr-2" />
            {saving ? "Saving..." : "Save & Update Inventory"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
